import { Button, Modal, Paper, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { makeStyles } from 'tss-react/mui';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import type { TranscriptionResponseDTO } from '@klangio/ai2notes-ts-client';

export interface TranscriptionTitleDialogProps {
  transcription?: TranscriptionResponseDTO;
  onSubmit: (newTitle: string) => void;
  handleOnClose: () => void;
  open: boolean;
}
const useStyles = makeStyles()((theme) => ({
  container: {
    boxShadow: `0px 0px 6px 2px ${theme.palette.primary.main}`,
    margin: '10em auto',
    padding: '1em',
    width: '23em',
    [theme.breakpoints.down('sm')]: {
      margin: ' auto',
    },
  },
  buttons: {
    display: 'flex',
    gap: 10,
    margin: '2em 0',
    justifyContent: 'end',
  },
}));

const TranscriptionTitleDialog = ({
  open,
  onSubmit,
  transcription,
  handleOnClose,
}: TranscriptionTitleDialogProps): JSX.Element => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const validationSchema = Yup.object({
    title: Yup.string().min(3, 'Min. 3 character').required('This field is required'),
  });

  const formik = useFormik({
    initialValues: {
      title: transcription?.title ?? '',
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values.title);
    },
  });

  return (
    <Modal open={open} onClose={handleOnClose}>
      <Paper className={classes.container}>
        <Typography variant="h6" component="h2" sx={{ padding: 2 }}>
          {t('home.input.dialog.title_composer.title_dialog_label')}
        </Typography>
        <form onSubmit={formik.handleSubmit}>
          <TextField
            id="title"
            name="title"
            label={t('home.input.dialog.title_composer.piece_title_label')}
            variant="outlined"
            margin="normal"
            fullWidth
            type="text"
            onChange={formik.handleChange}
            error={Boolean(formik.touched.title && formik.errors.title)}
            helperText={formik.touched.title && formik.errors.title}
            value={formik.values.title}
          />
          <div className={classes.buttons}>
            <Button
              id="btn_change_title"
              data-testid="title-edit"
              size="small"
              variant="contained"
              type="submit">
              {t('home.dialog.confirm')}
            </Button>
            <Button
              id="btn_change_title_cancel"
              data-testid="title-edit-cancel"
              size="small"
              variant="outlined"
              onClick={() => handleOnClose()}>
              {t('home.dialog.cancel')}
            </Button>
          </div>
        </form>
      </Paper>
    </Modal>
  );
};

export default TranscriptionTitleDialog;
